import apiClient from "./api";
import { VendorListing, SuspendVendorResponse, Vendor } from "../types";

export interface VendorFilters {
  types?: string[];
  items?: string[];
  occasions?: string[];
  featured?: boolean;
  status?: 'active' | 'suspended';
  operatingTimes?: {
    dayOfWeek?: number;
    timeSlot?: string;
  };
}

export interface VendorSort {
  by?: "newest" | "oldest" | "name" | "rating";
  ascending?: boolean;
}

export interface GetVendorsListingRequest {
  filters?: VendorFilters;
  sort?: VendorSort;
  limit?: number;
  page?: number;
  pageBased?: boolean;
}

export const VendorsListingService = {
  // Get vendors with cursor pagination
  getVendors: async (params: {
    filters?: VendorFilters;
    searchTerm?: string;
    limit?: number;
    lastEvaluatedKey?: string;
  }): Promise<{
    success: boolean;
    vendors: any[];
    pagination?: {
      hasMore?: boolean;
      lastEvaluatedKey?: string;
    };
  }> => {
    const requestBody: any = {
      filters: params.filters,
      limit: params.limit || 10,
    };

    if (params.searchTerm && params.searchTerm.trim().length > 0) {
      requestBody.searchTerm = params.searchTerm.trim();
    }

    if (params.lastEvaluatedKey) {
      requestBody.lastEvaluatedKey = params.lastEvaluatedKey;
    }

    console.log('POST /vendors/list - Request body:', JSON.stringify(requestBody, null, 2));

    const response = await apiClient.post(`/vendors/list`, requestBody);
    
    const responseData = response.data;
    console.log('📊 VendorsListingService.getVendors: Response:', responseData);
    
    return {
      success: true,
      vendors: responseData.data?.vendors || responseData.vendors || [],
      pagination: responseData.data?.pagination || responseData.pagination,
    };
  },

  // Get vendors listing using POST /admins/vendors/list
  getVendorsListing: async (
    page: number = 1,
    limit: number = 20,
    searchQuery: string = "",
    status?: "active" | "suspended",
    filters?: VendorFilters,
    sort?: VendorSort
  ): Promise<VendorListing> => {
    // Build filters object - merge provided filters with any additional filters
    const requestFilters: VendorFilters = {
      ...filters,
    };
    
    // Add status to filters if provided
    // Normalize status to match DynamoDB enum values (Active, Suspended)
    if (status) {
      // Capitalize first letter: "active" → "Active", "suspended" → "Suspended"
      requestFilters.status = (status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()) as 'active' | 'suspended';
    }
    
    // Build request body
    const requestBody: GetVendorsListingRequest = {
      filters: Object.keys(requestFilters).length > 0 ? requestFilters : undefined,
      sort: sort || {
        by: "newest",
        ascending: false,
      },
      limit,
      page,
      pageBased: true,
      ...(searchQuery && searchQuery.trim().length > 0 && { searchTerm: searchQuery.trim() })
    };

    console.log('POST /admins/vendors/list - Request body:', JSON.stringify(requestBody, null, 2));

    const response = await apiClient.post(
      `/vendors/list`,
      requestBody
    );
    
    // Handle different response structures
    const responseData = response.data;
    if (responseData.data) {
      console.log('📊 VendorsListingService: Response data:', responseData.data);
      return responseData.data;
    }
    return responseData;
  },
  // Suspend vendor
  suspendVendor: async (vendorId: string): Promise<SuspendVendorResponse> => {
    const response = await apiClient.patch<SuspendVendorResponse>(
      `/vendors/${vendorId}/suspend`
    );
    return response.data;
  },

  // Suspend active vendor (new simplified endpoint)
  suspendActiveVendor: async (vendorId: string): Promise<any> => {
    const response = await apiClient.post(
      `/admins/vendors/${vendorId}/suspend-active`
    );
    return response.data;
  },

  // Activate suspended vendor (new simplified endpoint)
  activateSuspendedVendor: async (vendorId: string): Promise<any> => {
    const response = await apiClient.post(
      `/admins/vendors/${vendorId}/activate-suspended`
    );
    return response.data;
  },

  // Update vendor fee percentage
  updateVendorFee: async (vendorId: string, feePercentage: number): Promise<any> => {
    const response = await apiClient.patch(
      `/admins/vendors/${vendorId}/fee`,
      { feePercentage }
    );
    return response.data;
  },

  // Get default platform fee percentage
  getDefaultFee: async (): Promise<{ defaultFeePercentage: number }> => {
    const response = await apiClient.get(`/admins/settings/default-fee`);
    console.log('Get default fee raw response:', response.data);
    
    // Handle different response structures
    if (response.data?.data?.defaultFeePercentage !== undefined) {
      return { defaultFeePercentage: response.data.data.defaultFeePercentage };
    }
    if (response.data?.defaultFeePercentage !== undefined) {
      return { defaultFeePercentage: response.data.defaultFeePercentage };
    }
    
    // No fallback - throw error if not found
    throw new Error('Default fee not found in database');
  },

  // Update default platform fee percentage
  updateDefaultFee: async (feePercentage: number): Promise<any> => {
    const response = await apiClient.patch(
      `/admins/settings/default-fee`,
      { feePercentage }
    );
    return response.data;
  },
  
  // Get vendor by ID - Use dedicated admin endpoint
  getVendorById: async (vendorId: string): Promise<Vendor | null> => {
    try {
      const response = await apiClient.get(`/admins/vendors/${vendorId}`);
      
      const responseData = response.data;
      console.log('📊 Get Vendor By ID Response:', responseData);
      
      // Handle response structure
      if (responseData.data?.vendorProfile) {
        const vendorProfile = responseData.data.vendorProfile;
        
        // Map the backend vendor profile to frontend Vendor type
        // Map services for display: prefer enriched type names when present,
        // otherwise keep catalog type keys (UI resolves via label maps).
        const enrichedTypeNames = vendorProfile.categoryTreeEnriched?.typeNames;
        const services = vendorProfile.categoryTree?.types?.map((type: string, index: number) => {
          const items = vendorProfile.categoryTree?.items;
          // items is a flat string[] of selected item names (not per-type)
          const itemsArray = Array.isArray(items) ? items : [];
          return {
            type: enrichedTypeNames?.[index] || type,
            items: itemsArray,
          };
        }) || vendorProfile.services || [];
        
        // Extract occasions from occasionsData object
        const occasions: string[] = [];
        if (vendorProfile.occasionsData && typeof vendorProfile.occasionsData === 'object') {
          Object.values(vendorProfile.occasionsData).forEach((occasion: any) => {
            if (occasion?.title) {
              occasions.push(occasion.title);
            }
          });
        }
        
        const vendor: Vendor = {
          vendorId: vendorProfile.vendorId,
          companyName: vendorProfile.companyName,
          companyDescription: vendorProfile.companyDescription,
          companyWebsite: vendorProfile.companyWebsite,
          contactName: vendorProfile.contactName || '',
          email: vendorProfile.email,
          phone: vendorProfile.phone,
          countryCode: vendorProfile.countryCode,
          profileImageUrl: vendorProfile.profileImageUrl,
          rating: vendorProfile.rating,
          displayOrder: vendorProfile.displayOrder,
          vendorCreatedAt: vendorProfile.vendorCreatedAt || vendorProfile.createdAt || Date.now(),
          status: vendorProfile.status,
          occasions: occasions,
          services,
          promotions: vendorProfile.promotions || [],
          availabilities: vendorProfile.availabilities || [],
          orderCount: vendorProfile.orderCount,
          totalEarnings: vendorProfile.totalEarnings,
          eventiFeePercentage: vendorProfile.eventiFeePercentage || vendorProfile.customFeePercentage,
          customFeePercentage: vendorProfile.customFeePercentage,
          reviewsCount: vendorProfile.reviewsCount,
          questionsCount: vendorProfile.questionsCount,
          // Add enriched category tree data
          categoryTreeEnriched: vendorProfile.categoryTreeEnriched,
          // Legal information
          tradeLicenseNumber: vendorProfile.tradeLicenseNumber,
          commercialRegistrationNo: vendorProfile.commercialRegistrationNo,
          registeredName: vendorProfile.registeredName,
          registeredAddress: vendorProfile.registeredAddress,
          // Service policy
          acceptOrdersDeadline: vendorProfile.acceptOrdersDeadline,
          acceptChangesDeadline: vendorProfile.acceptChangesDeadline,
          servicePolicy: vendorProfile.servicePolicy,
          // Locations served
          locationsServed: vendorProfile.locationsServed,
          // Shipping fees
          shippingFeeInsideDoha: vendorProfile.shippingFeeInsideDoha,
          shippingFeeOutsideDoha: vendorProfile.shippingFeeOutsideDoha,
          // Bank account information
          bankName: vendorProfile.bankName,
          bankAccountName: vendorProfile.bankAccountName,
          bankAccountNumber: vendorProfile.bankAccountNumber,
        };
        
        return vendor;
      }
      
      return null;
    } catch (error: any) {
      console.error('Error fetching vendor by ID:', error);
      
      // If 404, vendor not found
      if (error.response?.status === 404) {
        return null;
      }
      
      throw error;
    }
  },

  // Get vendor occasions for admin
  getVendorOccasions: async (vendorId: string): Promise<any> => {
    try {
      const response = await apiClient.get(`/admins/vendors/${vendorId}/occasions`);
      const responseData = response.data;
      
      console.log('📅 Vendor Occasions API Response:', responseData);
      
      // Handle different response structures
      if (responseData.data) {
        return responseData.data;
      }
      return responseData;
    } catch (error: any) {
      console.error('Error fetching vendor occasions:', error);
      throw error;
    }
  },

  // Get all categories catalogue (public endpoint, works for admin too)
  getAllCategories: async (): Promise<any> => {
    try {
      const response = await apiClient.get('/categories-catalogue');
      const responseData = response.data;
      
      console.log('📂 Categories Catalogue API Response:', responseData);
      
      // Handle different response structures
      if (responseData.data) {
        return responseData.data;
      }
      return responseData;
    } catch (error: any) {
      console.error('Error fetching categories catalogue:', error);
      throw error;
    }
  },

  // Get all occasions (public endpoint, works for admin too)
  getAllOccasions: async (limit: number = 100): Promise<any> => {
    try {
      const response = await apiClient.get('/occasions', {
        params: { limit },
      });
      const responseData = response.data;
      
      console.log('🎉 Occasions API Response:', responseData);
      
      // Handle different response structures
      if (responseData.data) {
        return responseData.data;
      }
      return responseData;
    } catch (error: any) {
      console.error('Error fetching occasions:', error);
      throw error;
    }
  },

  // Get vendor products
  getVendorProducts: async (vendorId: string): Promise<any> => {
    try {
      const response = await apiClient.get(`/vendors/${vendorId}/products`);
      const responseData = response.data;
      
      console.log('📦 Vendor Products API Response:', response);
      console.log('📦 Vendor Products Data:', responseData);
      
      // Handle different response structures
      if (responseData.data) {
        return responseData.data;
      }
      if (responseData.success && responseData.products) {
        return responseData.products;
      }
      if (Array.isArray(responseData)) {
        return responseData;
      }
      return responseData;
    } catch (error: any) {
      console.error('Error fetching vendor products:', error);
      throw error;
    }
  },

  // Get vendor's categories catalogue for admin
  getVendorCategoriesCatalogue: async (vendorId: string): Promise<any> => {
    try {
      const response = await apiClient.get(`/admins/vendors/${vendorId}/categories-catalogue`);
      const responseData = response.data;
      
      console.log('📂 Vendor Categories Catalogue API Response:', responseData);
      
      // Handle different response structures
      if (responseData.data) {
        return responseData.data;
      }
      return responseData;
    } catch (error: any) {
      console.error('Error fetching vendor categories catalogue:', error);
      throw error;
    }
  },
};

export default VendorsListingService;
